/**
 * Bloomreach autosuggest: show sliced variants instead of repeated titles.
 *
 * Suggest returns one row per sliced variant, but its payload only carries
 * title/pid/url/thumb_image/sale_price, with an always-empty `variants` array.
 * Every row therefore renders the same name and links to the bare product URL,
 * and carries no item number.
 *
 * Search (core API) returns the same slices with variant_title, option_1_value,
 * variant_id and pim_item_number, and both APIs serve the same thumbnail per
 * slice, so the thumbnail filename joins the two. Rows are looked up by their
 * product title rather than by the typed query: Suggest matches prefixes but
 * Search does not, so a mid-word query like "rain bird noz" returns zero Search
 * docs while the title does not. Titles also repeat across keystrokes, which
 * keeps the cache warm.
 *
 * Rows with no match are left exactly as Bloomreach rendered them.
 */
(function () {
  var FALLBACK_SEARCH_ENDPOINT = 'https://core.dxpapi.com/api/v1/core/';
  var SEARCH_FIELDS =
    'pid,title,variant_title,thumb_image,variant_id,option_1_value,pim_item_number';
  var SEARCH_ROWS = 40;

  var ROW = '.blm-autosuggest__result';
  var TITLE = '.blm-autosuggest-result-details__title';
  var PANEL = '.blm-autosuggest-search-results';
  var ITEM_NUMBER_CLASS = 'blm-autosuggest-result-details__item-number';

  var cache = {};

  function config() {
    return (window.bloomreachConnector && window.bloomreachConnector.config) || null;
  }

  /**
   * Suggest and core live on matching hosts per environment
   * (staging-suggest/staging-core, suggest/core), so deriving one from the
   * other keeps staging from querying the production catalog.
   */
  function searchEndpoint() {
    var cfg = config();
    var suggest = cfg && cfg.autosuggest && cfg.autosuggest.endpoint;

    return suggest ? suggest.replace(/suggest/g, 'core') : FALLBACK_SEARCH_ENDPOINT;
  }

  function text(value) {
    if (Array.isArray(value)) value = value.length ? value[0] : '';
    return String(value == null ? '' : value).trim();
  }

  // The CDN transform query differs between responses; the filename does not.
  function imageKey(url) {
    if (!url) return '';
    return String(url).split('?')[0].split('/').pop() || '';
  }

  function buildSearchUrl(query) {
    var cfg = config();
    if (!cfg || !cfg.account_id || !cfg.domain_key) return '';

    var params = {
      account_id: cfg.account_id,
      domain_key: cfg.domain_key,
      request_type: 'search',
      search_type: 'keyword',
      fl: SEARCH_FIELDS,
      q: query,
      rows: SEARCH_ROWS,
      start: 0,
      url: cfg.url || window.location.href,
      ref_url: cfg.ref_url || window.location.href,
      request_id: Date.now()
    };
    if (cfg.auth_key) params.auth_key = cfg.auth_key;
    if (cfg.tracking_cookie) params._br_uid_2 = cfg.tracking_cookie;

    return (
      searchEndpoint() +
      '?' +
      Object.keys(params)
        .map(function (key) {
          return encodeURIComponent(key) + '=' + encodeURIComponent(params[key]);
        })
        .join('&')
    );
  }

  /**
   * Thumbnail filename -> queue of { label, variantId, itemNumber }, restricted
   * to docs whose title matches the row exactly so a broad keyword match can't
   * pull in a different product. A queue rather than one entry so slices that
   * share artwork are consumed in order instead of collapsing onto the first
   * row.
   */
  function buildSliceMap(docs, title) {
    var wanted = title.toLowerCase();
    var map = {};

    (docs || []).forEach(function (doc) {
      if (text(doc.title).toLowerCase() !== wanted) return;

      var key = imageKey(text(doc.thumb_image));
      if (!key) return;

      var label = text(doc.option_1_value);
      var variantTitle = text(doc.variant_title);
      var variantId = text(doc.variant_id);
      var itemNumber = text(doc.pim_item_number);
      if (label === 'Default Title') label = '';
      if (!label && !variantTitle && !variantId && !itemNumber) return;

      if (!map[key]) map[key] = [];
      map[key].push({
        label: label,
        variantTitle: variantTitle,
        variantId: variantId,
        itemNumber: itemNumber
      });
    });

    return map;
  }

  function slicesForTitle(title) {
    var key = title.toLowerCase();
    if (cache[key]) return cache[key];

    var url = buildSearchUrl(title);
    if (!url) return Promise.resolve(null);

    cache[key] = fetch(url)
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (json) {
        var docs = json && json.response && json.response.docs;
        return docs ? buildSliceMap(docs, title) : null;
      })
      .catch(function () {
        // Drop the rejected promise so a transient failure can be retried.
        delete cache[key];
        return null;
      });

    return cache[key];
  }

  function withVariant(href, variantId) {
    if (!href || !variantId) return href || '';
    if (href.indexOf('variant=') !== -1) return href;
    return href + (href.indexOf('?') === -1 ? '?' : '&') + 'variant=' + encodeURIComponent(variantId);
  }

  /**
   * variant_title already reads as a full product name, brand included, so it
   * replaces the row title outright. Appending the option value is only a
   * fallback for variants that have no variant_title.
   */
  function renderTitle(row, slice) {
    var title = row.querySelector(TITLE);
    if (!title) return;

    if (slice.variantTitle) {
      title.textContent = slice.variantTitle;
      return;
    }

    if (slice.label && title.textContent.indexOf(slice.label) === -1) {
      title.textContent = title.textContent.trim() + ' - ' + slice.label;
    }
  }

  // Sits between the title and the price, matching the item number shown on
  // the search and collection cards.
  function renderItemNumber(row, itemNumber) {
    if (!itemNumber || row.querySelector('.' + ITEM_NUMBER_CLASS)) return;

    var title = row.querySelector(TITLE);
    if (!title) return;

    var el = document.createElement('div');
    el.className = ITEM_NUMBER_CLASS;
    el.textContent = itemNumber;
    title.insertAdjacentElement('afterend', el);
  }

  function applySlices(rows, map) {
    if (!map) return;

    // How many rows have already claimed each thumbnail.
    var used = {};

    rows.forEach(function (row) {
      if (row.hasAttribute('data-slice-applied')) return;

      var img = row.querySelector('img');
      var key = imageKey(img && (img.getAttribute('src') || img.src));
      var queue = key ? map[key] : null;
      if (!queue || !queue.length) return;

      var offset = used[key] || 0;
      used[key] = offset + 1;
      var slice = queue[Math.min(offset, queue.length - 1)];

      renderTitle(row, slice);
      renderItemNumber(row, slice.itemNumber);

      if (slice.variantId) {
        [].forEach.call(row.querySelectorAll('a[href]'), function (link) {
          link.setAttribute('href', withVariant(link.getAttribute('href'), slice.variantId));
        });
      }

      row.setAttribute('data-slice-applied', 'true');
    });
  }

  function enrich(panel) {
    var pending = [].slice.call(panel.querySelectorAll(ROW + ':not([data-slice-applied])'));
    if (!pending.length) return;

    var byTitle = {};
    pending.forEach(function (row) {
      var el = row.querySelector(TITLE);
      var title = el ? el.textContent.trim() : '';
      if (!title) return;
      (byTitle[title] = byTitle[title] || []).push(row);
    });

    Object.keys(byTitle).forEach(function (title) {
      var rows = byTitle[title];
      Promise.resolve(slicesForTitle(title)).then(function (map) {
        // Bloomreach may have re-rendered while the lookup was in flight.
        applySlices(
          rows.filter(function (row) {
            return row.isConnected;
          }),
          map
        );
      });
    });
  }

  function observe(panel) {
    if (panel.dataset.sliceObserver === 'true') return;
    panel.dataset.sliceObserver = 'true';

    new MutationObserver(function () {
      enrich(panel);
    }).observe(panel, { childList: true, subtree: true });

    enrich(panel);
  }

  function scan() {
    [].forEach.call(document.querySelectorAll(PANEL), observe);
  }

  function init() {
    scan();
    // Bloomreach creates the panel lazily on first focus.
    new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
